/**
 * SIL-2169 - the "Escrow.com payments" panel on /profile (business orgs).
 * Replaces the mock payment-methods list: SiliconX stores no card or bank
 * details, buyers pay each offer through its Escrow.com link, and Escrow.com
 * matches the parties by email.
 *
 * The email shown per side is the one the Escrow.com processor sends: the
 * org's buyer_org / seller_org contact email (escrow_offer_facts, migration
 * 1017). It comes from the profile the page already loaded.
 *
 * KYB (SIL-2171): "Buyer KYB" and "Seller KYB" rows from the shared `orgKybState()`.
 * Buyer status is read through the shared `useBuyerStatus()` hook (one cache
 * entry, see hooks/useBuyerStatus.ts); on error or while loading the buyer row is
 * omitted rather than guessed. "Cleared by SiliconX" means the org's buyer
 * verification is verified. SiliconX collects no seller KYB yet, so the seller row
 * is a constant. `buyerAwaitingOpsClearance` (GET /api/client/me/escrow-status,
 * migration 1172) adds a deal-level line while an accepted offer waits for the
 * manual ops tick; absent/null hides it.
 *
 * `hasOpenEscrowTransaction` comes from GET /api/client/me/escrow-status
 * (get_my_org_open_escrow_transaction, migration 1167): true while the org has
 * a live Escrow.com transaction on either side. The contact email is then
 * locked, in the UI and in the database (EC008). Absent means unknown, and the
 * warning stays hidden.
 */
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { KYB_AWAITING_OPS_LINE, orgKybState } from '@siliconx/shared';
import { useBuyerStatus } from '../../hooks/useBuyerStatus';

export const ESCROW_COM_URL = 'https://www.escrow.com';

export const ESCROW_EMAIL_LOCKED_MESSAGE =
  'You have an open Escrow.com transaction. This email is how Escrow.com matches you to it, so it cannot be changed until the transaction closes.';

function EmailRow({ label, email, testId }: { label: string; email: string | null | undefined; testId: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span data-testid={testId} className="break-all text-right text-foreground">
        {email ? email : <span className="text-muted-foreground">Not set</span>}
      </span>
    </div>
  );
}

export function EscrowPaymentsPanel({
  buyerEmail,
  sellerEmail,
  hasOpenEscrowTransaction = false,
  buyerAwaitingOpsClearance = false,
}: {
  buyerEmail: string | null | undefined;
  sellerEmail: string | null | undefined;
  hasOpenEscrowTransaction?: boolean;
  buyerAwaitingOpsClearance?: boolean;
}) {
  const { data: kyb } = useBuyerStatus();

  return (
    <Card className="space-y-4" data-testid="escrow-payments-panel">
      <div>
        <PanelTitle title="Escrow.com payments" />
        <p className="mt-1 text-xs text-muted-foreground">
          Offer deposits and balances are paid through Escrow.com.
        </p>
      </div>

      <div className="space-y-2 rounded-md border border-border bg-muted p-3">
        <p className="text-xs font-medium text-muted-foreground">Email Escrow.com uses for your org</p>
        <EmailRow label="Buyer profile" email={buyerEmail} testId="escrow-buyer-email" />
        <EmailRow label="Seller profile" email={sellerEmail} testId="escrow-seller-email" />
        <p className="text-xs text-muted-foreground">
          This is the contact email on each profile above. Escrow.com matches you to a transaction by this email.
        </p>
        {hasOpenEscrowTransaction && (
          <p
            role="alert"
            data-testid="escrow-open-transaction-warning"
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          >
            {ESCROW_EMAIL_LOCKED_MESSAGE}
          </p>
        )}
      </div>

      <div data-testid="escrow-buyer-copy">
        <p className="text-xs font-medium text-muted-foreground">If you buy</p>
        <p className="mt-1 text-sm text-foreground">
          You pay the deposit and the balance through the Escrow.com link in each offer. SiliconX never stores your
          card or bank details.
        </p>
      </div>

      <div data-testid="escrow-seller-copy">
        <p className="text-xs font-medium text-muted-foreground">If you sell</p>
        <p className="mt-1 text-sm text-foreground">
          Create an Escrow.com account with your seller contact email and set up payouts there. Escrow.com pays you
          out through that account.
        </p>
        <a
          href={ESCROW_COM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-block text-sm font-medium text-brand-700 hover:underline"
        >
          Go to Escrow.com
        </a>
      </div>

      <div className="space-y-2 border-t border-border pt-3" data-testid="escrow-kyb-status">
        <p className="text-xs font-medium text-muted-foreground">Business verification (KYB)</p>
        {kyb && (
          <div className="flex items-baseline justify-between gap-3 text-sm" data-testid="escrow-kyb-buyer">
            <span className="text-xs font-medium text-muted-foreground">Buyer KYB</span>
            <span className="text-right text-foreground">{orgKybState('buyer', { buyerStatus: kyb.status }).label}</span>
          </div>
        )}
        <div className="flex items-baseline justify-between gap-3 text-sm" data-testid="escrow-kyb-seller">
          <span className="text-xs font-medium text-muted-foreground">Seller KYB</span>
          <span className="text-right text-foreground">{orgKybState('seller').label}</span>
        </div>
        {buyerAwaitingOpsClearance && (
          <p data-testid="escrow-kyb-awaiting-ops" className="text-xs text-muted-foreground">
            {KYB_AWAITING_OPS_LINE}
          </p>
        )}
      </div>
    </Card>
  );
}
